<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.PACKAGES.DELIVERY_CREATED"
    :handler="onDeliveryCreated"
  />

  <DialogComponent ref="dialog" type="modal" @closed="onClosed">
    <template #default>
      <div class="text-center">
        <!-- Success Icon with animation -->
        <div class="flex justify-center mb-6">
          <div
            class="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30"
          >
            <svg
              class="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
        <!-- Title -->
        <h3 class="text-2xl font-bold text-gray-800 mb-2">Livraison créée !</h3>
        <p class="text-gray-500 text-sm mb-6">
          Votre demande de livraison a été enregistrée avec succès
        </p>

        <!-- Tracking Code Card -->
        <div
          class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 mb-4 border-2 border-slate-200"
        >
          <p
            class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
          >
            Numéro de tracking
          </p>
          <div class="flex items-center justify-center gap-3">
            <span class="text-2xl font-bold text-slate-800 tracking-widest">
              {{ trackingCode }}
            </span>
            <button
              @click="copyTrackingCode"
              class="p-2 rounded-xl bg-white hover:bg-slate-50 border-2 border-slate-200 hover:border-emerald-500 transition-all duration-300 group"
              :class="{ 'border-emerald-500 bg-emerald-50': copiedCode }"
            >
              <svg
                v-if="!copiedCode"
                class="w-5 h-5 text-slate-600 group-hover:text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <svg
                v-else
                class="w-5 h-5 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </button>
          </div>
          <p
            v-if="copiedCode"
            class="text-xs text-emerald-600 font-medium mt-2"
          >
            Code copié !
          </p>

          <button
            @click="copyTrackingLink"
            class="mt-4 w-full py-2 bg-white text-slate-700 rounded-xl text-sm font-medium border border-slate-200 hover:bg-slate-50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            Copier le lien de suivi
          </button>
        </div>

        <!-- Payment Link Card (If not paid) -->
        <div
          v-if="isPaymentPending && isMobileMoney && paymentLinkEnabled"
          class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-orange-100 animate-pulse-subtle"
        >
          <p
            class="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2"
          >
            Paiement en attente
          </p>
          <p class="text-xs text-orange-800/70 mb-4 px-2">
            Partagez ce lien pour permettre le paiement du colis
          </p>
          <button
            @click="copyPaymentLink"
            class="w-full py-3 bg-orange-600 text-white rounded-xl text-sm font-bold shadow-md shadow-orange-600/20 hover:bg-orange-700 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            Copier lien de paiement
          </button>
        </div>

        <!-- Mobile Money Direct Payment Instructions (If mobile money and not payment link) -->
        <div
          v-if="isPaymentPending && isMobileMoney && !paymentLinkEnabled"
          class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 mb-6 border-2 border-amber-200 text-left"
        >
          <div class="flex items-center gap-2.5 mb-2.5">
            <div
              class="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p
                class="text-xs font-bold text-amber-900 uppercase tracking-wide"
              >
                Validation du paiement
              </p>
              <p class="text-[11px] text-amber-700">
                Confirmez la transaction sur votre téléphone
              </p>
            </div>
          </div>

          <div
            class="text-xs text-slate-700 space-y-2 bg-white/90 rounded-xl p-3.5 border border-amber-200/70"
          >
            <p class="font-medium text-slate-800 leading-snug">
              Veuillez confirmer la demande de paiement sur votre téléphone.
            </p>
            <p class="text-[11px] text-slate-500">
              Si vous ne recevez pas la demande de paiements automatique, tapez :
            </p>
            <div class="space-y-1.5 pt-1">
              <div
                class="flex items-center justify-between bg-amber-50/70 p-2 rounded-lg border border-amber-200"
              >
                <span class="font-bold text-amber-900 text-xs"
                  >Pour MTN Money :</span
                >
                <span
                  class="font-mono text-xs font-bold bg-white px-2 py-0.5 rounded border border-amber-300 text-amber-900"
                  >*880*8# <span class="font-normal text-[10px] text-slate-500">puis 1</span></span
                >
              </div>
              <div
                class="flex items-center justify-between bg-blue-50/70 p-2 rounded-lg border border-blue-200"
              >
                <span class="font-bold text-blue-900 text-xs"
                  >Pour Moov money :</span
                >
                <span
                  class="font-mono text-xs font-bold bg-white px-2 py-0.5 rounded border border-blue-300 text-blue-900"
                  >*855*8# <span class="font-normal text-[10px] text-slate-500">puis 1</span></span
                >
              </div>
            </div>
            <p class="text-[10px] text-slate-400 italic pt-0.5">
              Sélectionnez 1 et validez manuellement avec votre code secret.
            </p>
          </div>
        </div>

        <!-- Package Info Summary - Route avec détours -->
        <div
          v-if="deliveryData"
          class="bg-white rounded-xl p-4 mb-6 border border-slate-200 text-left"
        >
          <div class="flex flex-col">
            <!-- Départ -->
            <div class="flex items-start gap-3">
              <div class="flex flex-col items-center flex-shrink-0">
                <div class="w-2.5 h-2.5 rounded-full bg-primary mt-1"></div>
                <div
                  class="w-0.5 flex-1 bg-primary/30 my-1 min-h-[1.25rem]"
                ></div>
              </div>
              <div class="pb-2 flex-1 min-w-0">
                <div
                  class="text-[10px] font-bold text-primary uppercase tracking-wider"
                >
                  Départ
                </div>
                <div class="text-sm font-semibold text-slate-800 truncate">
                  {{ deliveryData?.pickup?.address || '-' }}
                </div>
                <div
                  v-if="deliveryData.pickupContactName"
                  class="text-xs text-slate-400"
                >
                  {{ deliveryData.pickupContactName
                  }}{{
                    deliveryData.pickupContactPhone
                      ? ' · ' + deliveryData.pickupContactPhone
                      : ''
                  }}
                </div>
              </div>
            </div>

            <!-- Détours / Checkpoints -->
            <template v-if="deliveryData.checkpoints?.length">
              <div
                v-for="(cp, index) in deliveryData.checkpoints"
                :key="'cp-' + index"
                class="flex items-start gap-3"
              >
                <div class="flex flex-col items-center flex-shrink-0">
                  <div
                    class="w-2.5 h-2.5 rounded-full bg-orange-400 mt-1"
                  ></div>
                  <div
                    class="w-0.5 flex-1 bg-primary/30 my-1 min-h-[1.25rem]"
                  ></div>
                </div>
                <div class="pb-2 flex-1 min-w-0">
                  <div
                    class="text-[10px] font-bold text-orange-500 uppercase tracking-wider"
                  >
                    Détour {{ index + 1 }}
                  </div>
                  <div class="text-sm font-semibold text-slate-800 truncate">
                    {{ cp.address || '-' }}
                  </div>
                  <div v-if="cp.contactName" class="text-xs text-slate-400">
                    {{ cp.contactName }}{{ cp.phone ? ' · ' + cp.phone : '' }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Destination -->
            <div class="flex items-start gap-3">
              <div class="flex flex-col items-center flex-shrink-0">
                <div class="w-2.5 h-2.5 rounded-full bg-green-500 mt-1"></div>
              </div>
              <div class="flex-1 min-w-0">
                <div
                  class="text-[10px] font-bold text-green-600 uppercase tracking-wider"
                >
                  Destination
                </div>
                <div class="text-sm font-semibold text-slate-800 truncate">
                  {{ deliveryData?.destination?.address || '-' }}
                </div>
                <div
                  v-if="deliveryData.destinationContactName"
                  class="text-xs text-slate-400"
                >
                  {{ deliveryData.destinationContactName
                  }}{{
                    deliveryData.destinationContactPhone
                      ? ' · ' + deliveryData.destinationContactPhone
                      : ''
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Prix et distance -->
          <div
            class="flex justify-between items-center mt-3 pt-3 border-t border-slate-100"
          >
            <div class="flex items-center gap-3 text-xs text-slate-500">
              <span v-if="deliveryData.distanceKm"
                >📍 {{ deliveryData.distanceKm }} km</span
              >
              <span v-if="deliveryData.checkpoints?.length"
                >🔀 {{ deliveryData.checkpoints.length }} détour{{
                  deliveryData.checkpoints.length > 1 ? 's' : ''
                }}</span
              >
            </div>
            <span
              v-if="deliveryData.pricing?.total || deliveryData.price"
              class="font-bold text-slate-800"
            >
              {{
                formatAmount(deliveryData.pricing?.total || deliveryData.price)
              }}
            </span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-3">
          <!-- View Tracking Button -->
          <button
            @click="openTracking"
            class="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl font-bold text-lg shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            Vérifier le suivi
          </button>

          <!-- Close Button -->
          <button
            @click="close"
            class="w-full py-3.5 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all duration-300"
          >
            Fermer
          </button>
        </div>
      </div>
    </template>
  </DialogComponent>
</template>

<script setup>
import { ref, computed } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import DialogComponent from '@ui/dialog/DialogComponent.global.vue';
import Commons from '@features/shared/libraries/commons';

const dialog = ref(null);
const deliveryData = ref(null);
const trackingCode = ref('');
const copiedCode = ref(false);
const copiedLink = ref(false);

const isPaymentPending = computed(() => {
  const p = deliveryData.value?.payment;
  return p && p.status === 'pending';
});

const isMobileMoney = computed(() => {
  const p = deliveryData.value?.payment;
  const method = (p?.method || deliveryData.value?.paymentMethod || '').toLowerCase();
  return method === 'mobile_money' || method === 'mobilemoney' || method === 'momo';
});

const paymentLinkEnabled = computed(() => {
  return deliveryData.value?.metadata?.paymentLinkEnabled;
});

const paymentLink = computed(() => {
  const id = deliveryData.value?.id;
  const baseUrl =
    constants?.URLS?.packagePaymentBaseUrl || window.location.origin;
  return id ? `${baseUrl}/pay/package/${id}` : '';
});

// Full tracking URL for sharing
const trackingLink = computed(() => {
  const code = trackingCode.value;
  const baseUrl =
    constants?.URLS?.packageTrackingBaseUrl || window.location.origin;
  return code ? `${baseUrl}/track/package/${code}` : '';
});

function onDeliveryCreated(data) {
  console.log('[PackageCreatedSuccessDialog] Delivery created:', data);
  deliveryData.value = data;
  trackingCode.value =
    data?.reference || data?.trackingCode || data?.deliveryCode || '';
  copiedCode.value = false;

  if (trackingCode.value) {
    dialog.value?.show();
  }
}

function close() {
  dialog.value?.hide();
}

function onClosed() {
  copiedCode.value = false;
  copiedLink.value = false;
}

function copyTrackingCode() {
  Commons.copyToClipboard(trackingCode.value, () => {
    copiedCode.value = true;
    setTimeout(() => (copiedCode.value = false), 2000);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'success',
      title: 'Code copié',
      body: 'Le code a été copié dans le presse-papier'
    });
  });
}

function copyTrackingLink() {
  if (!trackingLink.value) return;
  Commons.copyToClipboard(trackingLink.value, () => {
    copiedLink.value = true;
    setTimeout(() => (copiedLink.value = false), 2000);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'success',
      title: 'Lien de suivi copié',
      body: 'Le lien de suivi a été copié dans le presse-papier'
    });
  });
}

function copyPaymentLink() {
  if (!paymentLink.value) return;
  Commons.copyToClipboard(paymentLink.value, () => {
    copiedLink.value = true;
    setTimeout(() => (copiedLink.value = false), 2000);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'success',
      title: 'Lien copié',
      body: 'Le lien de paiement a été copié dans le presse-papier'
    });
  });
}

function openTracking() {
  if (trackingCode.value) {
    const baseUrl = constants.APP_URL || 'https://app.monrapidoo.com';
    const trackingUrl = `${baseUrl}/track/package/${trackingCode.value}`;
    window.open(trackingUrl, '_blank');
  }
}

function formatAmount(amount) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(amount);
}
</script>
